import { successToast } from "Modules/DPToast";
import { useMutation } from "@apollo/client";
import { CreateAdminFormFields } from "Shared/types/Admins";
import mutations from "Shared/mutations";
import queries from "Shared/queries";
import { useForm } from "react-hook-form";
import { usePartnerConfig } from "Hooks/usePartnerConfig";
import { handleValidationErrors } from "Modules/HandleValidationErrors";
import {
  CreateManagerInput,
  CreateManagerMutation,
  CreateManagerMutationVariables,
  EmployerPermissions,
} from "generated/graphql";

type UseCreateAdminFormProps = {
  handleClose: () => void;
  page: number;
};

const useCreateAdminForm = ({ handleClose, page }: UseCreateAdminFormProps) => {
  const useFormMethodsAndData = useForm<CreateAdminFormFields>({
    mode: "onBlur",
  });
  const { getValues, trigger: triggerValidation } = useFormMethodsAndData;

  const [createManagerFn] = useMutation<CreateManagerMutation, CreateManagerMutationVariables>(
    mutations.CREATE_MANAGER
  );

  const { companies } = usePartnerConfig();

  const onSubmitFn = async (createAdminInput: CreateManagerInput) => {
    const response = await createManagerFn({
      refetchQueries: [{
        query: queries.GET_ADMINS_QUERY,
        variables: { page },
      }],
      variables: { createManagerInput: createAdminInput },
    });
    return response.data;
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.persist();
    const formData = getValues();
    const isFormValid = await triggerValidation();

    if (isFormValid) {
      const createAdminInput: CreateManagerInput = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber.replace(/([\s-)(])/gi, ""),
        permissions: formData.permissions.value as EmployerPermissions,
        companyIds: (formData.companyIds || []).map(companyId => companyId.value),
        locationIds: formData.locations?.length
          ? formData.locations.map(location => location.value)
          : undefined,
      };

      if (
        !createAdminInput.companyIds!.length ||
          createAdminInput.companyIds!.length === companies.length
      ) {
        delete createAdminInput.companyIds;
      }

      try {
        const response = await onSubmitFn(createAdminInput);
        handleClose();
        if (
          response &&
          response.createManager &&
          response.createManager.message
        ) {
          successToast(response.createManager.message, false);
        }
      } catch (err) {
        handleValidationErrors(err);
      }
    }
  };

  return { onSubmit, useFormMethodsAndData };
};

export default useCreateAdminForm;
